import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiRefreshCw } from "react-icons/fi";
import { Modal } from "./Modal";

// How often to re-check the running build (also checked on tab focus / becoming visible).
const POLL_MS = 2 * 60 * 1000;

/** The running build's identity: the manual version marker + the auto build stamp. Null on error. */
const fetchSignature = async (): Promise<string | null> => {
	try {
		const r = await axios.get("/api/version", { withCredentials: true });
		const d = r.data ?? {};
		const sig = `${d.version ?? ""}:${d.build ?? ""}`;
		return sig === ":" ? null : sig;
	} catch {
		return null;
	}
};

/**
 * Watches the admin API build (GET /api/version → version + build stamp). The build stamp changes on
 * every redeployed image, so when it differs from the one this tab loaded with, a new update has shipped
 * and this prompts the user to reload (or dismiss until an even newer build arrives). Polls every couple
 * of minutes and whenever the tab regains focus, so an open tab never runs stale for long.
 */
export const VersionWatcher: React.FC = () => {
	const initialRef = useRef<string | null>(null); // the build this tab loaded with
	const latestRef = useRef<string | null>(null); // the most recent build seen from the server
	const dismissedRef = useRef<string | null>(null); // a build the user chose to keep running
	const [available, setAvailable] = useState(false);

	const check = useCallback(async () => {
		const sig = await fetchSignature();
		if (!sig) return;
		latestRef.current = sig;
		if (initialRef.current === null) {
			initialRef.current = sig; // establish the baseline on first successful read
			return;
		}
		if (sig !== initialRef.current && sig !== dismissedRef.current) {
			setAvailable(true);
		}
	}, []);

	useEffect(() => {
		check();
		const id = window.setInterval(check, POLL_MS);
		const onFocus = () => check();
		const onVisible = () => {
			if (document.visibilityState === "visible") check();
		};
		window.addEventListener("focus", onFocus);
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			window.clearInterval(id);
			window.removeEventListener("focus", onFocus);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [check]);

	// Keep running the current build; don't prompt again until a NEWER one ships.
	const later = () => {
		dismissedRef.current = latestRef.current;
		setAvailable(false);
	};

	return (
		<Modal show={available} onHide={later} size="sm" centered>
			<Modal.Header closeButton>
				<Modal.Title>Nouvelle version disponible</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className="text-sm text-slate-700">
					Une mise à jour de l'application a été déployée. Rechargez la page pour l'appliquer.
				</p>
				<p className="mt-2 text-xs text-slate-400">
					Enregistrez votre travail avant de recharger : les modifications non enregistrées seront perdues.
				</p>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={later}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					Plus tard
				</button>
				<button
					type="button"
					onClick={() => window.location.reload()}
					className="inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004b8a]"
				>
					<FiRefreshCw className="h-4 w-4" /> Recharger
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default VersionWatcher;
